import React, { useEffect, useState, useRef } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningIcon from '@mui/icons-material/Warning';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import InfoIcon from '@mui/icons-material/Info';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Button,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { useNotifications } from '../../../contexts/user-context/notification-context';
import { NotificationTemplate } from '../../../interfaces/common/notifications';
import { useSnackBar } from '../../../contexts/snackbar';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationPermissionPrompt from '../notification-permissions';

export const templateIconMap: Record<NotificationTemplate, JSX.Element> = {
  [NotificationTemplate.PICKUP_ASSIGNED]: <AssignmentTurnedInIcon color="primary" />,
  [NotificationTemplate.DELIVERY_ASSIGNED]: <LocalShippingIcon color="primary" />,
  [NotificationTemplate.PAYMENT_ASSIGNED]: <PaymentIcon color="primary" />,
  [NotificationTemplate.SYSTEM_ALERT]: <WarningIcon color="error" />,
  [NotificationTemplate.CUSTOM_NOTIFICATION]: <InfoIcon color="info" />,
  [NotificationTemplate.DRIVER_ASSIGNED_TO_PICKUP_YOUR_ORDERS]: <AssignmentTurnedInIcon color="primary" />,
  [NotificationTemplate.DRIVER_UNASSIGNED_FROM_PICKUP_YOUR_ORDERS]: <AssignmentTurnedInIcon color="disabled" />,
  [NotificationTemplate.DRIVER_ASSIGNED_FOR_PAYMENT]: <PaymentIcon color="primary" />,
  [NotificationTemplate.DRIVER_COMPLETED_PAYMENT]: <PaymentIcon color="success" />,
  [NotificationTemplate.DRIVER_DROPPED_ORDERS_AT_WAREHOUSE]: <LocalShippingIcon color="secondary" />,
};

const NotificationsBell: React.FC = () => {
  const { unreadCount, markAsRead, dismissNotification, dismissAllNotifications, markAllAsRead, notifications } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount} color="secondary">
          {unreadCount ? <NotificationsActiveIcon fontSize='large' color={"warning"}/> : <NotificationsIcon fontSize='large' sx={{color: "white"}} />}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ style: { width: 450, maxHeight: 500, padding: '0.5rem' } }}
      >
        <NotificationPermissionPrompt />
        {notifications.length === 0 && <MenuItem disabled>No notifications</MenuItem>}

        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {notifications.map(n => (
            <MenuItem
              key={n.id}
              sx={{ flexDirection: 'row', alignItems: 'flex-start', py: 1, '&:hover': { backgroundColor: 'action.hover' } }}
            >
              {/* Template Icon */}
              <Box sx={{ mr: 1, mt: 0.5 }}>
                {templateIconMap[n.templateName] || <InfoIcon color="disabled" />}
              </Box>

              {/* Notification content */}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1" fontWeight={!n.isRead ? 'bold' : 'normal'} sx={{ display: 'flex', alignItems: 'center' }}>
                  {!n.isRead && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'primary.main', mr: 1 }} />}
                  {n.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'normal', mt: 0.3 }}>
                  {n.body}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                  {new Date(n.timestamp).toLocaleString()}
                </Typography>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                {!n.isRead && (
                  <Tooltip title="Mark as read">
                    <IconButton size="small" onClick={() => markAsRead(n.id)} color="primary">
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Dismiss">
                  <IconButton size="small" onClick={() => dismissNotification(n.id)} color="error">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </MenuItem>
          ))}
        </Box>

        <Divider sx={{ my: 1 }} />

        {notifications.length > 0 && (
          <MenuItem>
            <Box display="flex" gap={2} justifyContent="space-around" width="100%">
              <Button fullWidth variant="text" onClick={() => { markAllAsRead(); handleClose(); }}>
                Mark all as read
              </Button>
              <Button fullWidth variant="text" onClick={() => { dismissAllNotifications(); handleClose(); }}>
                Dismiss all
              </Button>
            </Box>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default NotificationsBell;
